// The first-time touch tutorial (#67): four short steps pointing at the
// stick and the buttons, about 20 seconds in all. Each step moves on when the
// player does it, or on its own after `STEP_MS`. Skippable, and remembered
// in localStorage so it only plays once.
//
// Sim-free and DOM-free: `ui/TouchControls.tsx` feeds it what the fingers are
// doing (`TouchPad.view()`) and a clock, and draws the current step.

import type { TouchButton, TouchView } from "./touch";

export interface TutorialStep {
  id: "steer" | "ollie" | "trick" | "grind";
  text: string;
  /** What it points at: the stick's spot, or a key. */
  target: "stick" | TouchButton;
}

export const TUTORIAL_STEPS: readonly TutorialStep[] = [
  { id: "steer", text: "Drag here to steer. You roll on your own, drag down to brake", target: "stick" },
  { id: "ollie", text: "Hold Ollie to crouch, let go to pop", target: "ollie" },
  { id: "trick", text: "In the air: Flip or Grab, plus a stick direction for more tricks", target: "flip" },
  { id: "grind", text: "Grind lights up near a rail: tap it to lock on, then L or R to hop off", target: "grind" },
];

/** How long a step waits before moving on by itself. Four of them make the 20 seconds. */
export const STEP_MS = 5000;

/** How far the stick has to move to count as steering, in pixels. */
const STEER_PX = 12;

export class TouchTutorial {
  index = 0;
  private elapsed = 0;
  /** The ollie step waits for Ollie to go down and then come up (the pop). */
  private ollieDown = false;

  get done(): boolean {
    return this.index >= TUTORIAL_STEPS.length;
  }

  get step(): TutorialStep | null {
    return TUTORIAL_STEPS[this.index] ?? null;
  }

  /** What the fingers are doing now. True when that finished the step. */
  touch(view: TouchView): boolean {
    const step = this.step;
    if (!step) return false;
    let did = false;
    if (step.id === "steer") {
      const s = view.stick;
      did = !!s && Math.hypot(s.kx - s.ox, s.ky - s.oy) >= STEER_PX;
    } else if (step.id === "ollie") {
      if (view.held.has("ollie")) this.ollieDown = true;
      else did = this.ollieDown;
    } else if (step.id === "trick") {
      did = view.held.has("flip") || view.held.has("grab");
    } else {
      did = view.held.has("grind");
    }
    if (did) this.next();
    return did;
  }

  /** Time passes; a step moves on by itself after `STEP_MS`. True when it moved. */
  tick(ms: number): boolean {
    if (this.done) return false;
    this.elapsed += ms;
    if (this.elapsed < STEP_MS) return false;
    this.next();
    return true;
  }

  skip(): void {
    this.index = TUTORIAL_STEPS.length;
  }

  private next(): void {
    this.index++;
    this.elapsed = 0;
    this.ollieDown = false;
  }
}

const SEEN_KEY = "kickflip.touchTutorial";

/** Whether the tutorial has played on this device. No storage: say it has, rather than replay it every load. */
export function tutorialSeen(store: Pick<Storage, "getItem"> | null): boolean {
  if (!store) return true;
  try {
    return store.getItem(SEEN_KEY) === "done";
  } catch {
    return true;
  }
}

export function markTutorialSeen(store: Pick<Storage, "setItem"> | null): void {
  try {
    store?.setItem(SEEN_KEY, "done");
  } catch {
    // Storage refused: it plays again next time, which is harmless.
  }
}
